import { ErrorBoundary as RollbarErrorBoundary, Provider as RollbarProvider } from '@rollbar/react'
import { LocaleProvider, QueryClientProvider } from '@systemeio/file-manager'
import { TipTapLocaleProvider } from '@systemeio/tip-tap'
import { getLocaleByLanguageCode, LanguageCodeEnum, SharedLocaleProvider } from '@systemeio/ui'
import { useState } from 'react'
import { DndProvider } from 'react-dnd'
import { ErrorBoundary } from 'react-error-boundary'
import { useTranslation } from 'react-i18next'
import { SkeletonTheme } from 'react-loading-skeleton'
import './App.css'
import Content from './components/core/Content'
import Header from './components/core/Header'
import PopupLibrary from './components/core/PopupLibrary'
import Sidebar from './components/core/Sidebar'
import { ExitUrlContext } from './context/ExitUrlContext'
import { IsTemplateContext } from './context/isTemplateContext'
import rollbarConfig from './rollbar'
import { ModifiedBackend } from './utils/conditionalDndBackend'

function ErrorFallback({ error, componentStack, resetErrorBoundary }: any) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <pre>{componentStack}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  )
}

export type AppProps = {
  id: number
  isTemplate: boolean
}

function App({ id, isTemplate }: AppProps) {
  const [exitUrl, setExitUrl] = useState('')
  const { i18n } = useTranslation()

  if (!id) {
    window.location.assign(window.location.origin)
    return <div>Not found</div>
  }

  const languageCode = i18n.language as LanguageCodeEnum
  const locale = getLocaleByLanguageCode(languageCode)

  return (
    <RollbarProvider config={rollbarConfig}>
      <RollbarErrorBoundary>
        <SkeletonTheme baseColor="#ccc" highlightColor="#f5f5f5">
          <IsTemplateContext.Provider value={isTemplate}>
            <ExitUrlContext.Provider value={exitUrl}>
              <ErrorBoundary FallbackComponent={ErrorFallback}>
                <SharedLocaleProvider locale={locale}>
                  <LocaleProvider languageCode={languageCode}>
                    <TipTapLocaleProvider languageCode={languageCode}>
                      <QueryClientProvider>
                        <DndProvider backend={ModifiedBackend}>
                          <Header />
                          <div className="flex h-[calc(100vh-69px)] overflow-y-hidden">
                            <Sidebar />
                            <Content isTemplate={isTemplate} id={id} setExitUrl={setExitUrl} />
                          </div>
                          <PopupLibrary />
                        </DndProvider>
                      </QueryClientProvider>
                    </TipTapLocaleProvider>
                  </LocaleProvider>
                </SharedLocaleProvider>
              </ErrorBoundary>
            </ExitUrlContext.Provider>
          </IsTemplateContext.Provider>
        </SkeletonTheme>
      </RollbarErrorBoundary>
    </RollbarProvider>
  )
}

export default App
